import { type ApiResponse } from "apisauce"

import { request } from "../../../services/api"
import type { BasePagination } from "../../../types/api.ts"
import type {
  Conversation,
  ConversationMembers,
  Message,
  NotificationLevel,
  Participant,
  ProjectConversations,
  SearchResult
} from "../types/messaging.types.ts"

const BASE = "/messaging"

/**
 * The portal reaches the same conversations through its own prefix. Both
 * surfaces are the same endpoints server-side; the prefix is the second lock
 * that keeps a customer-facing screen from ever addressing an internal room.
 */
export const messagingPaths = (portal = false) => {
  const root = portal ? `${BASE}/portal` : BASE
  return {
    root,
    socket: (accessToken: string) =>
      `${import.meta.env.VITE_APP_BASE_API_WS_ROUTE}/messaging/?token=${accessToken}`,
    conversations: `${root}/conversations/`,
    conversation: (id: string) => `${root}/conversations/${id}/`,
    messages: (id: string) => `${root}/conversations/${id}/messages/`,
    read: (id: string) => `${root}/conversations/${id}/read/`,
    settings: (id: string) => `${root}/conversations/${id}/settings/`,
    draft: (id: string) => `${root}/conversations/${id}/draft/`,
    participants: (id: string) => `${root}/conversations/${id}/participants/`,
    mentionable: (id: string) => `${root}/conversations/${id}/mentionable/`,
    unreadTotal: `${root}/conversations/unread-total/`,
    message: (id: string) => `${root}/messages/${id}/`,
    messageContext: (id: string) => `${root}/messages/${id}/context/`,
    deletePreview: `${BASE}/messages`,
    search: `${root}/search/`,
    directory: `${BASE}/directory/`,
    direct: `${BASE}/conversations/direct/`,
    projectConversations: (projectId: string) =>
      `${BASE}/projects/${projectId}/conversations/`,
    projectGroups: (projectId: string) => `${BASE}/projects/${projectId}/groups/`,
    members: (id: string) => `${BASE}/conversations/${id}/members/`,
    portalProjectConversation: (projectId: string) =>
      `${BASE}/portal/projects/${projectId}/conversation/`,
    attachmentDownload: (id: string) =>
      `${root}/attachments/${id}/download/?download=true`,
    saveToProjectFiles: (id: string) =>
      `${BASE}/attachments/${id}/save-to-project-files/`
  }
}

export interface MessagePage {
  results: Message[]
  has_more: boolean
}

export const getConversations = (
  params: {
    kind?: string
    record_kind?: string
    record?: string
    unread?: string
    q?: string
  },
  portal = false
): Promise<ApiResponse<Conversation[]>> =>
  request.api.get(messagingPaths(portal).conversations, params)



export const getConversation = (
  id: string,
  portal = false
): Promise<ApiResponse<Conversation>> =>
  request.api.get(messagingPaths(portal).conversation(id))

export const getMessages = (
  id: string,
  params: { before?: string; after?: string },
  portal = false
): Promise<ApiResponse<MessagePage>> =>
  request.api.get(messagingPaths(portal).messages(id), params)

/**
 * Always multipart: a send may carry files, and switching content type by
 * payload shape is how a composer ends up with two code paths that drift.
 */
export const sendMessage = (
  id: string,
  payload: {
    body: string
    reply_to?: string
    client_token: string
    attachments?: File[]
  },
  portal = false,
  /** Real bytes-sent progress, so the bar reflects the upload rather than a guess. */
  onProgress?: (percent: number) => void
): Promise<ApiResponse<Message>> => {
  const form = new FormData()
  form.append("body", payload.body)
  form.append("client_token", payload.client_token)
  if (payload.reply_to) form.append("reply_to", payload.reply_to)
  payload.attachments?.forEach((file) => form.append("attachments", file))
  return request.api.post(messagingPaths(portal).messages(id), form, {
    // The axios instance defaults every request to `application/json`
    // (`services/api.ts`), so a FormData body goes out labelled JSON with no
    // multipart boundary and the server cannot parse the parts — the message
    // arrives, the files do not. Every other upload path in this app sets this
    // header for the same reason.
    headers: { "Content-Type": "multipart/form-data" },
    onUploadProgress: (event: { loaded: number; total?: number }) => {
      if (!onProgress || !event.total) return
      onProgress(Math.round((event.loaded / event.total) * 100))
    }
  })
}

export const editMessage = (
  id: string,
  body: string,
  portal = false
): Promise<ApiResponse<Message>> =>
  request.api.patch(messagingPaths(portal).message(id), { body })

export const deleteMessage = (
  id: string,
  portal = false
): Promise<ApiResponse<Message>> =>
  request.api.delete(messagingPaths(portal).message(id))

export const getDeletePreview = (
  id: string
): Promise<
  ApiResponse<{ attachment_count: number; kept_in_project_files: string[] }>
> => request.api.get(`${BASE}/messages/${id}/delete-preview/`)

export const getMessageContext = (
  id: string,
  portal = false
): Promise<
  ApiResponse<{ conversation_id: string; target_id: string; results: Message[] }>
> => request.api.get(messagingPaths(portal).messageContext(id))

/**
 * The API renders timestamps without an offset ("2026-10-01 20:28:34"). Echoing
 * one straight back read as a *naive* instant on the server, which is a
 * different moment depending on who is asking — so the marker is normalised to
 * an unambiguous ISO 8601 instant here rather than left to interpretation.
 */
const asInstant = (value: string): string => {
  const parsed = new Date(value.includes("T") ? value : value.replace(" ", "T") + "Z")
  return Number.isNaN(parsed.getTime()) ? value : parsed.toISOString()
}

export const markConversationRead = (
  id: string,
  upTo: string,
  portal = false,
  /** The message actually reached. Exact, where the timestamp is rounded. */
  upToMessage?: string
): Promise<ApiResponse<{ last_read_at: string; unread_count: number }>> =>
  request.api.post(messagingPaths(portal).read(id), {
    up_to: asInstant(upTo),
    ...(upToMessage ? { up_to_message: upToMessage } : {})
  })

export const setNotificationLevel = (
  id: string,
  level: NotificationLevel,
  portal = false
): Promise<ApiResponse<{ notification_level: NotificationLevel }>> =>
  request.api.patch(messagingPaths(portal).settings(id), {
    notification_level: level
  })

export const saveDraft = (
  id: string,
  draft: string,
  portal = false
): Promise<ApiResponse<{ draft: string }>> =>
  request.api.put(messagingPaths(portal).draft(id), { draft })

export const getMentionable = (
  id: string,
  q: string,
  portal = false
): Promise<ApiResponse<Participant[]>> =>
  request.api.get(messagingPaths(portal).mentionable(id), { q })

export const getUnreadTotal = (
  portal = false
): Promise<ApiResponse<{ unread_total: number }>> =>
  request.api.get(messagingPaths(portal).unreadTotal)

export const searchMessages = (
  params: Record<string, string | undefined>,
  portal = false
): Promise<ApiResponse<BasePagination<SearchResult>>> =>
  request.api.get(messagingPaths(portal).search, params)

/** Who the caller may message — on `record` when one is chosen, since a direct
 *  message belongs to a record and its participants come from that record. */
export const getDirectory = (
  q: string,
  record?: string
): Promise<ApiResponse<Participant[]>> =>
  request.api.get(messagingPaths().directory, { q, record })

export const openDirectConversation = (
  userId: string,
  projectId: string
): Promise<ApiResponse<Conversation>> =>
  request.api.post(messagingPaths().direct, {
    user_id: userId,
    project_id: projectId
  })

export const getProjectConversations = (
  projectId: string
): Promise<ApiResponse<ProjectConversations>> =>
  request.api.get(messagingPaths().projectConversations(projectId))

export const createContributorGroup = (
  projectId: string,
  payload: { name: string; member_ids: string[] }
): Promise<ApiResponse<Conversation>> =>
  request.api.post(messagingPaths().projectGroups(projectId), payload)

export const getConversationMembers = (
  id: string
): Promise<ApiResponse<ConversationMembers>> =>
  request.api.get(messagingPaths().members(id))

export const updateConversationMembers = (
  id: string,
  payload: { add?: string[]; remove?: string[] }
): Promise<ApiResponse<ConversationMembers>> =>
  request.api.post(messagingPaths().members(id), payload)

export const getPortalProjectConversation = (
  projectId: string
): Promise<
  ApiResponse<{
    conversation: Conversation
    customer_setup: ProjectConversations["customer_setup"]
  }>
> => request.api.get(messagingPaths().portalProjectConversation(projectId))

export const saveAttachmentToProjectFiles = (
  attachmentId: string,
  payload: {
    folder: string
    document_type?: string
    file_type?: number
    visible_to_customer: boolean
  }
): Promise<ApiResponse<{ document_id: string; folder_id: string }>> =>
  request.api.post(messagingPaths().saveToProjectFiles(attachmentId), payload)

/**
 * The rooms of a project-conversations payload, as a flat list.
 *
 * Written defensively on purpose: the project page loads this only to put a
 * number on the Messages tab, and a payload missing a key — an older backend,
 * a partial response — must not take the whole project screen down with it.
 */
export const roomsOf = (
  data: Partial<ProjectConversations> | null | undefined
): Conversation[] =>
  [
    data?.team_chat,
    data?.customer_chat,
    ...(data?.groups ?? []),
    ...(data?.direct_chats ?? [])
  ].filter((room): room is Conversation => Boolean(room))

/**
 * An attachment's bytes, fetched through the authorized client.
 *
 * The download route is JWT-protected, and neither an `<img src>` nor an
 * `<a href>` carries the Authorization header — the browser just issues a bare
 * GET, which the API refuses (and which, when the API is on another origin,
 * silently returns the SPA's own HTML instead). So the bytes come back here and
 * the caller turns them into an object URL. The permission check is unchanged:
 * this is the same authorized endpoint, asked properly.
 */
export const fetchAttachmentBlob = (
  attachmentId: string,
  portal = false
): Promise<ApiResponse<Blob>> =>
  request.api.get(
    `${messagingPaths(portal).root}/attachments/${attachmentId}/download/`,
    {},
    { responseType: "blob" }
  )
